/** Onboarding queries: runs, run detail, draft items. */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type OnboardingDraftItem,
  type OnboardingRun,
  onboardingDraftItemSchema,
  onboardingRunSchema,
} from '../responses';
import { unwrap } from './_helpers';

export type DraftItemsFilters = {
  status?: 'pending_review' | 'accepted' | 'edited' | 'rejected';
  requiredField?: boolean;
};

/** `GET /onboarding/runs` — onboarding runs for the hotel. */
export function useOnboardingRuns(
  tenantId: string,
  hotelId: string,
): UseQueryResult<OnboardingRun[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.onboardingRuns(tenantId, hotelId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/onboarding/runs',
        { params: { path: { tenantId, hotelId } }, parseAs: 'json' },
      );
      return unwrap(result, z.array(onboardingRunSchema));
    },
  });
}

/** `GET /onboarding/runs/:runId`. */
export function useOnboardingRun(
  tenantId: string,
  hotelId: string,
  runId: string,
): UseQueryResult<OnboardingRun> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.onboardingRun(tenantId, hotelId, runId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/onboarding/runs/{runId}',
        { params: { path: { tenantId, hotelId, runId } }, parseAs: 'json' },
      );
      return unwrap(result, onboardingRunSchema);
    },
    enabled: runId.length > 0,
  });
}

/** `GET /onboarding/runs/:runId/draft-items?status=&requiredField=`. */
export function useDraftItems(
  tenantId: string,
  hotelId: string,
  runId: string,
  filters?: DraftItemsFilters,
): UseQueryResult<OnboardingDraftItem[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.onboardingDraftItems(tenantId, hotelId, runId, filters),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/onboarding/runs/{runId}/draft-items',
        {
          params: {
            path: { tenantId, hotelId, runId },
            query: { status: filters?.status, requiredField: filters?.requiredField },
          },
          parseAs: 'json',
        },
      );
      return unwrap(result, z.array(onboardingDraftItemSchema));
    },
    enabled: runId.length > 0,
  });
}
